import { Button, Input } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import { CloseIcon } from '@mm/icons';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { colors } from '@mm/utils';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CheckIcon } from '../../../../../common/component-icons/CheckIcon/CheckIcon';
import styles from './AudienceNameEditBar.module.css';
import { useAudienceNameValidation } from './use-audience-name-validation';

interface Props {
  activeAudience: AudiencesModel;
  onEndEditing: () => void;
  className?: string;
}
export const AudienceNameEditBar = (props: Props) => {
  const { activeAudience, onEndEditing, className } = props;
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const [newAudienceName, setNewAudienceName] = useState(activeAudience.name);
  const trimmedNewAudienceName = newAudienceName.trim();
  const debouncedAudienceName = useDebounce(trimmedNewAudienceName, 250);
  const { validateAudienceName } = useAudienceNameValidation();
  const [newAudienceNameStatus, setNewAudienceNameStatus] = useState<{
    dirty: boolean;
    valid?: boolean;
    saving?: boolean;
  }>({ dirty: false });
  const newAudienceNameChanged =
    trimmedNewAudienceName !== activeAudience?.name;
  useEffect(
    () =>
      setNewAudienceNameStatus({
        dirty: newAudienceNameChanged,
        valid:
          newAudienceNameChanged && trimmedNewAudienceName
            ? undefined
            : !!trimmedNewAudienceName,
      }),
    [trimmedNewAudienceName, newAudienceNameChanged],
  );
  const onRenameAudience = async () => {
    if (!newAudienceNameStatus.dirty) {
      onEndEditing();
      return;
    }
    setNewAudienceNameStatus(status => ({ ...status, saving: true }));
    try {
      await audiencesCRUDActions.update(
        { ...activeAudience, name: debouncedAudienceName },
        activeChatbotId,
      );
      setNewAudienceNameStatus({ dirty: false });
      onEndEditing();
    } finally {
      setNewAudienceNameStatus(status => ({ ...status, saving: false }));
    }
  };
  useEffect(() => {
    if (debouncedAudienceName) {
      setNewAudienceNameStatus(status => ({ ...status, checking: true }));
      (async () => {
        const valid = await validateAudienceName(debouncedAudienceName);
        setNewAudienceNameStatus(status => ({
          ...status,
          valid,
          checking: false,
        }));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAudienceName]);

  return (
    <div className={classNames(className, styles.container)}>
      <Input
        css={{ container: styles.input }}
        type="text"
        invalid={
          newAudienceNameStatus.dirty && newAudienceNameStatus.valid === false
        }
        onChange={e => setNewAudienceName(e.target.value)}
        value={newAudienceName}
      />
      <Button
        color="gray"
        leftIcon={<CloseIcon color={colors.red} />}
        onClick={onEndEditing}
      />
      <Button
        disabled={!newAudienceNameStatus.valid || newAudienceNameStatus.saving}
        onClick={onRenameAudience}
        color="secondary-green"
        leftIcon={
          <CheckIcon
            color={newAudienceNameStatus ? colors.green : colors.darkGray}
          />
        }
      />
      {(newAudienceNameStatus.saving ||
        newAudienceNameStatus.valid === undefined) && (
        <div>
          <i className="fas fa-spinner fa-spin" />
        </div>
      )}
      {trimmedNewAudienceName &&
        newAudienceNameStatus.dirty &&
        newAudienceNameStatus.valid === false && (
          <div className={styles.error}>
            An audience with that name already exists
          </div>
        )}
    </div>
  );
};
